import { useEffect, useRef, useState } from 'react';
import { ApiError, jobs } from '../lib/api';
import type { Job, JobState } from '../lib/types';

const ACTIVE_STATES: JobState[] = ['pending', 'enqueued', 'running', 'awaiting_callback'];
const BASE_INTERVAL = 2500;

interface PollResult {
  job: Job | null;
  error: ApiError | null;
  loading: boolean;
}

/**
 * Poll a job until it reaches a settled state (awaiting_user_input / complete / failed / dlq).
 * Backs off on 429 using Retry-After. Pass jobId=null to idle.
 */
export function useJobPoll(jobId: string | null): PollResult {
  const [job, setJob] = useState<Job | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!jobId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);
    setJob(null);
    setError(null);

    const schedule = (ms: number) => {
      timerRef.current = setTimeout(run, ms);
    };

    async function run() {
      try {
        const next = await jobs.get(jobId as string, controller.signal);
        if (cancelled) return;
        setJob(next);
        setError(null);
        setLoading(false);
        if (ACTIVE_STATES.includes(next.state)) schedule(BASE_INTERVAL);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (err instanceof ApiError) {
          setError(err);
          setLoading(false);
          // Keep polling through transient rate-limits; stop on 401/404.
          if (err.isRateLimited) schedule((err.retryAfter ?? 60) * 1000);
          else if (err.status === 0) schedule(BASE_INTERVAL * 2);
        } else {
          setLoading(false);
        }
      }
    }

    void run();
    return () => {
      cancelled = true;
      controller.abort();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [jobId]);

  return { job, error, loading };
}
